import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../../LayoutWrapper/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { getDriverIdFromSession } from "../../utils/auth_storage";
import { getApihandler, putApihandler } from "../../ApiHandler";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { buildMediaUrl, formatDateForInput } from "../../utils/media_url";
import DocUploadHero from "../../Components/DocUploadHero";
import "../../CSS/UserLogin/index.css";
import "../../CSS/DocumentUpload/index.css";

const CHECK_ITEMS = [
  {
    key: "license",
    label: "License info",
    isDone: (state) => state.licenceNumber && state.licenceExpiryDate,
  },
  {
    key: "files",
    label: "License image uploaded",
    isDone: (state) => state.hasLicenceImage,
  },
];

export default function PersonalDocument() {
  const [licenceNumber, setLicenceNumber] = useState("");
  const [licenceExpiryDate, setLicenceExpiryDate] = useState("");
  const [licenceImage, setLicenceImage] = useState(null);
  const [licenceImagePreview, setLicenceImagePreview] = useState("");
  const [savedLicence, setSavedLicence] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { session } = useAuth();
  const driverId = getDriverIdFromSession(session);

  const hasLicenceImage = Boolean(licenceImage || licenceImagePreview);
  const formState = {
    licenceNumber,
    licenceExpiryDate,
    hasLicenceImage,
  };

  const applyLicenceData = useCallback((licence) => {
    if (!licence) {
      return;
    }
    const normalizedNumber = (licence.licenceNumber || "").trim();
    const normalizedExpiry = formatDateForInput(licence.licenceExpiryDate);
    const imageKey = licence.licenceImage || "";
    setLicenceNumber(normalizedNumber);
    setLicenceExpiryDate(normalizedExpiry);
    setLicenceImagePreview(buildMediaUrl(imageKey));
    if (normalizedNumber && normalizedExpiry && imageKey) {
      setSavedLicence({
        licenceNumber: normalizedNumber,
        licenceExpiryDate: normalizedExpiry,
        licenceImageKey: imageKey,
      });
    }
  }, []);

  const canContinueWithoutSave = () => {
    if (!savedLicence?.licenceImageKey) {
      return false;
    }
    if (licenceImage) {
      return false;
    }
    return (
      licenceNumber.trim() === savedLicence.licenceNumber &&
      licenceExpiryDate === savedLicence.licenceExpiryDate
    );
  };

  const fetchLicenceDetails = useCallback(async () => {
    if (!driverId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const sessionLicence = session?.profile?.licenceDetails?.[0];
      if (sessionLicence) {
        applyLicenceData(sessionLicence);
      }
      let response = await getApihandler(`/getDriverLicenceDetails/${driverId}`);
      if (response?.status === 200 && response?.data) {
        applyLicenceData(response.data);
      } else {
        const fallback = await getApihandler(`/getDriverPersonalInfo/${driverId}`);
        if (fallback?.status === 200 && fallback?.data?.licenceDetails?.[0]) {
          applyLicenceData(fallback.data.licenceDetails[0]);
        }
      }
    } catch (error) {
      console.error("Failed to load licence details", error);
    } finally {
      setIsLoading(false);
    }
  }, [applyLicenceData, driverId, session?.profile?.licenceDetails]);

  useEffect(() => {
    if (!driverId) {
      navigate("/driver-login", { replace: true });
      return;
    }
    fetchLicenceDetails();
  }, [driverId, fetchLicenceDetails, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!driverId) {
      return;
    }
    if (!licenceNumber.trim() || !licenceExpiryDate) {
      swal({
        icon: "warning",
        text: "Please enter your license number and expiry date.",
      });
      return;
    }
    if (!hasLicenceImage) {
      swal({
        icon: "warning",
        text: "Please upload your license image.",
      });
      return;
    }
    if (canContinueWithoutSave()) {
      navigate("/cardocument");
      return;
    }
    if (!licenceImage && !savedLicence?.licenceImageKey) {
      swal({
        icon: "warning",
        text: "Please upload your license image.",
      });
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("licenceNumber", licenceNumber.trim());
    formData.append("licenceExpiryDate", licenceExpiryDate);
    if (licenceImage) {
      formData.append("licenceImage", licenceImage);
    }
    try {
      const response = await putApihandler(
        `/addDriverLicenceInfo/${driverId}`,
        formData
      );
      if (response?.status === 200) {
        swal({
          icon: "success",
          text: "Details saved successfully",
        });
        navigate("/cardocument");
      } else {
        const apiMessage =
          response?.message ||
          response?.error?.response?.data?.message ||
          "Unable to save licence details.";
        swal({
          icon: "error",
          title: "Please Try Again",
          text: apiMessage,
        });
      }
    } catch (error) {
      swal({
        icon: "error",
        title: "Please Try Again",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!driverId) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="doc-upload-wrapper">
        <div className="doc-upload-steps-card">
          <p className="doc-upload-steps-label">Driver onboarding · Step 1 of 2</p>
          <div className="doc-upload-steps">
            <span className="doc-upload-step doc-upload-step--active">
              <span className="doc-upload-step-num">1</span>
              <span>Personal documents</span>
            </span>
            <span className="doc-upload-step-line" aria-hidden="true" />
            <span className="doc-upload-step">
              <span className="doc-upload-step-num">2</span>
              <span>Vehicle documents</span>
            </span>
          </div>
        </div>

        <section className="doc-upload-page">
          <DocUploadHero
            variant="sidebar"
            badge={
              <>
                <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
                Driver onboarding
              </>
            }
            title="License details"
            subtitle="Add or update your driver license information for verification."
          />

          <div className="doc-upload-panel">
            <h2 className="doc-upload-card-title">Driver License Details</h2>
            <p className="doc-upload-card-subtitle">
              License number, expiry date, and license image are required. Accepted
              formats: JPG, PNG, PDF.
            </p>

            <div className="doc-upload-hint">
              <InfoOutlinedIcon />
              <span>
                Ensure photos are clear and all text is readable. Blurry or cropped
                images may delay approval.
              </span>
            </div>

            <div className="doc-upload-checklist">
              {CHECK_ITEMS.map((item) => {
                const done = item.isDone(formState);
                return (
                  <span
                    key={item.key}
                    className={`doc-upload-check ${done ? "doc-upload-check--done" : ""}`}
                  >
                    {done ? (
                      <CheckCircleOutlineIcon fontSize="inherit" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="inherit" />
                    )}
                    {item.label}
                  </span>
                );
              })}
            </div>

            {isLoading ? (
              <p className="doc-upload-loading">Loading your license details…</p>
            ) : (
              <form className="doc-upload-form" onSubmit={handleSubmit} noValidate>
                <div className="doc-upload-section">
                  <h3 className="doc-upload-section-title">
                    <BadgeOutlinedIcon />
                    License information
                  </h3>
                  <div className="doc-upload-fields">
                    <div className="doc-upload-row">
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="licence-number">
                          License Number
                        </label>
                        <div className="user-login-input-wrap">
                          <BadgeOutlinedIcon className="user-login-input-icon" />
                          <input
                            id="licence-number"
                            className="user-login-input"
                            type="text"
                            placeholder="e.g. D1234567"
                            required
                            value={licenceNumber}
                            onChange={(event) => setLicenceNumber(event.target.value)}
                          />
                        </div>
                      </div>
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="licence-expiry">
                          License Expiry Date
                        </label>
                        <div className="user-login-input-wrap">
                          <CalendarTodayOutlinedIcon className="user-login-input-icon" />
                          <input
                            id="licence-expiry"
                            className="user-login-input"
                            type="date"
                            required
                            value={licenceExpiryDate}
                            onChange={(event) =>
                              setLicenceExpiryDate(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="doc-upload-section">
                  <h3 className="doc-upload-section-title">
                    <CloudUploadOutlinedIcon />
                    License image
                  </h3>
                  <div className="doc-upload-file-row">
                    <div
                      className={`doc-upload-file-zone ${
                        licenceImage || licenceImagePreview
                          ? "doc-upload-file-zone--has-file"
                          : ""
                      }`}
                    >
                      <input
                        type="file"
                        name="licenceImage"
                        accept="image/*,.pdf"
                        onChange={(event) =>
                          setLicenceImage(event.target.files[0] || null)
                        }
                      />
                      <UploadFileOutlinedIcon className="doc-upload-file-icon" />
                      <p className="doc-upload-file-label">License Image</p>
                      <p className="doc-upload-file-name">
                        {licenceImage
                          ? licenceImage.name
                          : licenceImagePreview
                            ? "Uploaded — click to replace"
                            : "Click to upload"}
                      </p>
                    </div>
                  </div>
                  {licenceImagePreview && (
                    <div className="doc-upload-preview">
                      <img
                        src={licenceImagePreview}
                        alt="Current license"
                        className="doc-upload-preview-image"
                      />
                      <p className="doc-upload-preview-caption">
                        Current license on file
                      </p>
                    </div>
                  )}
                </div>

                <div className="doc-upload-actions">
                  <button
                    type="submit"
                    className="doc-upload-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="user-login-submit-spinner" />
                    ) : (
                      <ArrowForwardIcon fontSize="small" />
                    )}
                    {isSubmitting ? "Saving…" : "Continue to vehicle documents"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
